import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewContainerRef,
  AfterViewInit,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, ReplaySubject, merge, Subscription, fromEvent } from 'rxjs';
import { filter, pairwise, startWith, takeUntil } from 'rxjs/operators';

import { selectors as coreSelectors } from 'app/core/state';
import SocialStateHelper from 'app/modules/social/social-state-machine/social-state-helper';
import { selectors as socialSelectors } from 'app/modules/social/state/app';
import assertDefined from 'app/utils/assert-defined';
import { notNil } from 'app/utils/stream-util';
import User from 'entity/User';
import { SocialState } from 'messages/websocket.messages';

import { SelectDropdownComponent } from './select-dropdown/select-dropdown.component';
import { SelectOptionComponent } from './select-option/select-option.component';

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  standalone: false,
})
export class SelectComponent implements OnDestroy, AfterViewInit {
  @ContentChildren(SelectOptionComponent) private dropdownOptions?: QueryList<SelectOptionComponent>;
  @Input() public placeholder = '';
  @Output() public valueChange = new EventEmitter<string | number>();
  @Input() public value?: string | number;
  @Input() public roomCounter?: number;
  @ViewChild('dropdown') private dropdown?: SelectDropdownComponent;
  @Input() public visualIndicators = false;

  public open = false;
  private user?: User;

  private destroyed$ = new ReplaySubject<boolean>(1);
  private overlayRef?: OverlayRef;
  private dropdownOptionsSubscriptions: Subscription[] = [];

  public constructor(
    private overlay: Overlay,
    private elementRef: ElementRef<HTMLElement>,
    private viewContainerRef: ViewContainerRef,
    private store: Store,
  ) {
    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ user }) => (this.user = user));

    // Hide the options if invite to present is shown. Invite modal causes issues
    // with this modal's CDK Overlay. Also, hide it if fullscreen mode is enabled
    this.store
      .select(socialSelectors.getSocialSharedState)
      .pipe(
        startWith({} as SocialState),
        pairwise(),
        filter(
          ([prevState, state]) =>
            notNil(state) &&
            (SocialStateHelper.isUserInvited(state, this.user?.userId ?? 'invalid') ||
              SocialStateHelper.hasFullScreenBeenTriggered(state, prevState ?? ({} as SocialState))),
        ),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => this.destroyDropdown());

    fromEvent(window, 'resize')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        if (this.elementRef.nativeElement.offsetWidth === 0) {
          this.destroyDropdown();
        }
      });
  }

  public ngAfterViewInit(): void {
    this.updateSubscriptions();
    this.dropdownOptions?.changes.pipe(takeUntil(this.destroyed$)).subscribe(() => this.updateSubscriptions());
  }

  public updateSubscriptions(): void {
    this.cleanSubscriptions();
    this.dropdownOptions?.forEach((dropdownOption) => {
      const sub = dropdownOption.selected.pipe(takeUntil(this.destroyed$)).subscribe((option) => this.onSelect(option));
      this.dropdownOptionsSubscriptions.push(sub);
    });
  }

  private cleanSubscriptions(): void {
    this.dropdownOptionsSubscriptions.forEach((sub) => sub.unsubscribe);
  }

  public toggleDropdown(): void {
    if (this.open) {
      this.destroyDropdown();
    } else {
      this.openDropdown();
    }
  }

  public openDropdown(): void {
    this.open = true;
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.close(),
      width: this.elementRef.nativeElement.offsetWidth,
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.elementRef)
        .withPositions([
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
          },
        ]),
    });
    assertDefined(this.dropdown?.templateRef);
    const templatePortal = new TemplatePortal(this.dropdown.templateRef, this.viewContainerRef);
    this.overlayRef.attach(templatePortal);

    this.dropdownClosingActions(this.overlayRef)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.destroyDropdown());
  }

  public get selectedOptionLabel(): string {
    const selectedDropdown = this.dropdownOptions?.find((dropdownOption) => dropdownOption.value === this.value);
    return selectedDropdown?.label ?? this.placeholder;
  }

  public onSelect(option: DropdownOption): void {
    this.value = option.value;
    this.toggleDropdown();
    this.destroyDropdown();
    this.valueChange.emit(option.value);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.cleanSubscriptions();
    if (this.overlayRef) {
      this.overlayRef.dispose();
    }
  }

  private dropdownClosingActions(overlayRef: OverlayRef): Observable<MouseEvent | void> {
    const backdropClick$ = overlayRef.backdropClick();
    const detachment$ = overlayRef.detachments();

    return merge(backdropClick$, detachment$);
  }

  private destroyDropdown(): void {
    if (!this.overlayRef || !this.open) {
      return;
    }

    this.open = false;
    this.overlayRef.detach();
  }
}

export interface DropdownOption {
  label: string;
  value: string | number;
}
